import type { AddonGroup } from './menu'

/**
 * Selected addon for cart — used across guest app, dashboard cashier, and staff cashier.
 */
export interface SelectedAddon {
  id: string
  name: string
  price: number
  quantity: number
  groupName: string
}

/**
 * Compute the charged addon total using two-level free pricing:
 *
 * Phase 1 — Group-level: each group's `includedSelections` gives N free per group
 *           (highest-priced consumed first within each group)
 * Phase 2 — Item-level: `includedAddons` gives N free across all groups
 *           (highest-priced consumed first from remaining charged addons)
 *
 * @returns { addons, chargedTotal } — addons is the full list (for kitchen),
 *          chargedTotal is only the amount to charge the customer.
 */
export function computeAddonChargedTotal(
  addonGroups: AddonGroup[],
  selectedMap: Record<string, number>,
  itemIncludedAddons: number,
): { addons: SelectedAddon[]; chargedTotal: number } {
  const addons: SelectedAddon[] = []
  const afterGroupFree: { price: number; qty: number }[] = []

  // Phase 1: Group-level free
  for (const group of addonGroups) {
    const groupFree = group.includedSelections ?? 0
    const groupSelected: { id: string; name: string; price: number; qty: number }[] = []

    for (const opt of group.options ?? []) {
      const qty = selectedMap[opt.id] ?? 0
      if (qty > 0) {
        groupSelected.push({ id: opt.id, name: opt.name, price: opt.price, qty })
        addons.push({ id: opt.id, name: opt.name, price: opt.price, quantity: qty, groupName: group.name })
      }
    }

    // Consume free slots (highest price first)
    let freeLeft = groupFree
    const sorted = [...groupSelected].sort((a, b) => b.price - a.price)
    for (const s of sorted) {
      let remaining = s.qty
      let chargedQty = 0
      while (remaining > 0) {
        if (freeLeft > 0) freeLeft--
        else chargedQty++
        remaining--
      }
      if (chargedQty > 0) {
        afterGroupFree.push({ price: s.price, qty: chargedQty })
      }
    }
  }

  // Phase 2: Item-level free (across all groups)
  let itemFreeLeft = itemIncludedAddons
  let totalCharged = 0
  const sortedRemaining = [...afterGroupFree].sort((a, b) => b.price - a.price)
  for (const s of sortedRemaining) {
    let remaining = s.qty
    while (remaining > 0) {
      if (itemFreeLeft > 0) itemFreeLeft--
      else totalCharged += s.price
      remaining--
    }
  }

  return { addons, chargedTotal: Math.round(totalCharged * 100) / 100 }
}
